import { useDispatch, useSelector } from 'react-redux';

import {
  AUTHENTICATED_USER_ACCESS,
  TEAM_ARCHIVE_WRITE,
  TEAM_LIBRARY_WRITE,
  VIDEO_PUBLISH_ACCESS,
  VIDEO_SHARE_PRIVATE_ACTION,
  VIDEO_TOOLS_ACCESS,
} from 'constants/scopes';
import { SHOW_DOWNLOAD_VIDEO_INFO_MODAL } from 'constants/triggers';
import { selectCurrentUser } from 'selectors/user';
import individualFolderOptions from 'utilities/menuOptions/actions/individual/folder';
import individualVideoOptions from 'utilities/menuOptions/actions/individual/video';
import sharedOptions from 'utilities/menuOptions/actions/shared';
import {
  Folder,
  MenuContext,
  MenuOption,
  Video,
} from 'utilities/menuOptions/types';

import { useHasScope } from 'hooks/useHasScopes';

import { useShouldShowTrigger } from './triggers';
import { useGetSelectedWorkspace } from './workspace';

const MENU_OPTIONS = {
  bulk: {
    folder: [
      sharedOptions.MOVE,
      sharedOptions.ARCHIVE,
      sharedOptions.UNARCHIVE,
      sharedOptions.POST,
      sharedOptions.UNPOST,
      sharedOptions.DELETE,
    ],
    video: [
      sharedOptions.MOVE,
      sharedOptions.ARCHIVE,
      sharedOptions.UNARCHIVE,
      sharedOptions.POST,
      sharedOptions.UNPOST,
      sharedOptions.DELETE,
    ],
    item: [
      sharedOptions.MOVE,
      sharedOptions.ARCHIVE,
      sharedOptions.UNARCHIVE,
      sharedOptions.POST,
      sharedOptions.UNPOST,
      sharedOptions.DELETE,
    ],
  },
  individual: {
    folder: [
      sharedOptions.RENAME,
      sharedOptions.MOVE,
      individualFolderOptions.COPY_LINK,
      individualFolderOptions.MAKE_PRIVATE,
      individualFolderOptions.MAKE_PUBLIC,
      sharedOptions.DUPLICATE,
      sharedOptions.ARCHIVE,
      sharedOptions.UNARCHIVE,
      sharedOptions.POST,
      sharedOptions.UNPOST,
      sharedOptions.DELETE,
    ],
    video: [
      individualVideoOptions.SHARE,
      sharedOptions.RENAME,
      sharedOptions.MOVE,
      sharedOptions.DUPLICATE,
      sharedOptions.ARCHIVE,
      sharedOptions.UNARCHIVE,
      sharedOptions.POST,
      sharedOptions.UNPOST,
      individualVideoOptions.DOWNLOAD,
      sharedOptions.DELETE,
      individualVideoOptions.DIVE,
    ],
  },
};

const useBulkMenuOptions = (
  items: (Folder | Video)[],
  menuContext: MenuContext = {}
): MenuOption[] => {
  const dispatch = useDispatch();
  const hasArchiveAccessScope = useHasScope(TEAM_ARCHIVE_WRITE);
  const hasAuthenticatedAccess = useHasScope(AUTHENTICATED_USER_ACCESS);
  const hasTeamLibraryWriteScope = useHasScope(TEAM_LIBRARY_WRITE);
  const hasVideoToolsAccessScope = useHasScope(VIDEO_TOOLS_ACCESS);
  const hasVideoPublishAccessScope = useHasScope(VIDEO_PUBLISH_ACCESS);
  const hasVideoSharePrivateScope = useHasScope(VIDEO_SHARE_PRIVATE_ACTION);

  const currentUser = useSelector(selectCurrentUser);
  const selectedWorkspace = useGetSelectedWorkspace();

  const scopes = {
    hasArchiveAccessScope,
    hasAuthenticatedAccess,
    hasTeamLibraryWriteScope,
    hasVideoToolsAccessScope,
    hasVideoPublishAccessScope,
    hasVideoSharePrivateScope,
  };

  const extras = {
    shouldShowDownloadInfoModal: useShouldShowTrigger(
      SHOW_DOWNLOAD_VIDEO_INFO_MODAL
    ),
    // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
    downloadUrl: useSelector(state => state.share_video?.download_url),
  };

  if (!items.length) {
    return [];
  }

  const hasFolders = items.some(i => i.__typename === 'RegularUserFolder');
  const hasVideos = items.some(i => i.__typename === 'RegularUserVideo');
  const quantityType = items.length > 1 ? 'bulk' : 'individual';
  const assetType =
    hasVideos && hasFolders ? 'item' : hasFolders ? 'folder' : 'video';

  return MENU_OPTIONS[quantityType][assetType]
    .filter((menuOption: MenuOption): boolean =>
      menuOption.show({
        items,
        scopes,
        // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
        currentUser,
        selectedWorkspace,
        menuContext,
      })
    )
    .map(menuOption => ({
      ...menuOption,
      onClick: () => {
        menuOption.onClick({ dispatch, items, extras, menuContext });
      },
    }));
};

export default useBulkMenuOptions;
